import { LogManager } from '@src/utility/Logger';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { SceneData } from '@src/services/framing/SceneDetectionService';

const logger = LogManager.getLogger('store:SceneCreationHelper');

/**
 * Creates scenes from the first available scene detection for a story.
 * Uses preloaded scene detections if available, otherwise fetches from API.
 * @param storyId - The story ID to create scenes for
 * @param editor - The video editor instance
 * @param sceneDetections - Preloaded scene detections (if available)
 * @param preloadScenes - Function to preload scenes if not available
 * @returns Promise that resolves when scene creation is complete
 */
export async function createScenesFromDetection(
  storyId: string | undefined,
  editor: IVideoEditor | undefined,
  sceneDetections: SceneData[] | null,
  preloadScenes: (storyId: string) => Promise<void>,
  naturalMediaDimensions?: Record<string, NaturalDimensions>,
): Promise<void> {
  if (!storyId || !editor) {
    logger.warn('Cannot create scenes: missing storyId or editor');
    return;
  }

  let scenes = sceneDetections;

  // Try to load scenes if not already preloaded
  if (!scenes || scenes.length === 0) {
    logger.debug('No preloaded scene detections, attempting to load', {
      storyId,
    });
    await preloadScenes(storyId);
    // Note: We need to get the updated scenes after preloading
    // The caller should pass a getter or the preload should return them
  }

  // Re-check after potential preload - caller needs to provide updated value
  // For now we work with what we have
  if (!scenes || scenes.length === 0) {
    logger.debug('No scene detections available', { storyId });
    return;
  }

  // Use dynamic import to avoid circular dependency
  const { SceneDetectionService } =
    await import('@src/services/framing/SceneDetectionService');
  const sceneDetectionService = new SceneDetectionService();

  logger.debug('Creating scenes from detections', {
    storyId,
    sceneCount: scenes.length,
  });

  const sceneDetectionsForCommand =
    sceneDetectionService.convertToCommandFormat(scenes);
  const success = editor.commandCreateScenesByMediaTime(
    sceneDetectionsForCommand,
    naturalMediaDimensions,
  );

  if (success) {
    logger.info('Successfully created scenes', {
      storyId,
      sceneCount: sceneDetectionsForCommand.length,
    });
  } else {
    logger.debug('Scene creation failed', { storyId });
  }
}
